import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components';
import { useForm } from 'react-hook-form';

import configs from '@/config';
import { endpoints } from '@/react-query/endpoints';
import { mixpanelEvents } from '@/utils/mixpanel';

import {
  type HubSpotFieldOption,
  useHubSpotFormOptions,
} from '../hooks/useHubSpotFormOptions';
import { useSubmitToHubSpot } from '../hooks/useSubmitToHubSpot';
import { preQualifySchema } from '../schema';
import { trackPreQualifyEvent } from '../trackPreQualifyEvent';
import type { PreQualifyFormValues } from '../types';
import { shouldShowDownload } from '../utils';

interface PreQualifyFormProps {
  dealId: string;
  initialValues: Partial<PreQualifyFormValues>;
  onSuccess: (values: PreQualifyFormValues) => void;
}

export const PreQualifyForm = ({
  dealId,
  initialValues,
  onSuccess,
}: PreQualifyFormProps) => {
  const form = useForm<PreQualifyFormValues>({
    resolver: zodResolver(preQualifySchema),
    defaultValues: {
      firstName: initialValues.firstName ?? '',
      lastName: initialValues.lastName ?? '',
      companyName: initialValues.companyName ?? '',
      email: initialValues.email ?? '',
      phone: initialValues.phone ?? '',
      itContactEmail: initialValues.itContactEmail ?? '',
      productRequest: initialValues.productRequest ?? '',
      primaryPms: initialValues.primaryPms ?? '',
      imagingPlatform: initialValues.imagingPlatform ?? '',
    },
  });

  const { mutate: submitToHubSpot, isLoading, error } = useSubmitToHubSpot();
  const { data: formOptions } = useHubSpotFormOptions();

  // Pre-fill the form with the initial values if they are valid
  // We need this because the form details are loaded async in @useHubSpotFormOptions, and we need to ensure the form is pre-filled with the correct values
  useEffect(() => {
    if (!formOptions) return;
    const dropdownFields = [
      'productRequest',
      'primaryPms',
      'imagingPlatform',
    ] as const;
    for (const field of dropdownFields) {
      const prefilled = initialValues[field];
      if (
        prefilled &&
        formOptions[field]?.some((opt) => opt.value === prefilled)
      ) {
        form.setValue(field, prefilled);
      }
    }
  }, [formOptions, initialValues, form]);

  const onSubmit = (values: PreQualifyFormValues) => {
    const qualifierLink = shouldShowDownload(values)
      ? `${configs.CONNECTOR_API_URL}/${endpoints.connector.downloadCompatibility}?code=${dealId}`
      : '';
    submitToHubSpot(
      { dealId, values, qualifierLink },
      {
        onSuccess: () => {
          trackPreQualifyEvent(dealId, mixpanelEvents.preQualifyFormSubmit, {
            dealId,
            companyName: values.companyName,
            productRequest: values.productRequest,
            primaryPms: values.primaryPms,
            imagingPlatform: values.imagingPlatform,
          });
          onSuccess(values);
        },
      },
    );
  };

  const renderSelectItems = (options: HubSpotFieldOption[] | undefined) =>
    options?.map((opt) => (
      <SelectItem key={opt.value} value={opt.value}>
        {opt.label}
      </SelectItem>
    ));

  const requiredMark = <span className="vai-text-red-500">*</span>;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="vai-space-y-4">
        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name {requiredMark}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name {requiredMark}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Business {requiredMark}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email {requiredMark}</FormLabel>
              <FormControl>
                <Input type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone {requiredMark}</FormLabel>
              <FormControl>
                <Input type="tel" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="itContactEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>IT Contact Email {requiredMark}</FormLabel>
              <FormControl>
                <Input type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="productRequest"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Request {requiredMark}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {renderSelectItems(formOptions?.productRequest)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="primaryPms"
          render={({ field }) => (
            <FormItem>
              <FormLabel>PMS System {requiredMark}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {renderSelectItems(formOptions?.primaryPms)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="imagingPlatform"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Imaging System {requiredMark}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {renderSelectItems(formOptions?.imagingPlatform)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {error != null && (
          <p className="vai-text-sm vai-text-red-500">
            Something went wrong. Please try again.
          </p>
        )}

        <Button type="submit" disabled={isLoading} className="vai-w-full">
          {isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </form>
    </Form>
  );
};
